<template>
  <div class="window-manager-container">
    <div class="inset-fade-effect" :dark="$vuetify.theme.dark" />
    <div ref="windowManager" class="window-manager custom-scrollbar">
      <div class="windows" :class="{ 'include-minimized-windows': minimizedWindowInteractions.length }">
        <transition-group name="list" tag="div" class="windows-container">
          <EcsWindow
            v-for="interaction in openWindowInteractions"
            :key="interaction.interactionId"
            :ref="`ecs-window-${interaction.interactionId}`"
            :interaction-id="interaction.interactionId"
            :title="interaction.formattedCustomerFullName"
            :minimized-height="minimizedHeight"
            :minimized-width="minimizedWidth"
            :restored-height="restoredHeight"
            :restored-width="restoredWidth"
            item-class="window-item"
            item-content-class="window-item-content"
            @maximized-window="handleWindowMaximized"
            @minimized-window="handleWindowMinimized"
            @window-mounted="handleOpenWindowMount"
            @closed-window="handleOpenWindowClosed"
          >
            <div class="interaction-wrapper surface lighten-1">
              <InteractionHeader :interaction="interaction" />
              <InteractionToolbar :interaction="interaction" />
              <InteractionContent :interaction="interaction" />
              <InteractionFooter :interaction="interaction" />
            </div>
          </EcsWindow>
        </transition-group>
      </div>
      <div
        class="minimized-windows"
        :class="{ active: minimizedWindowInteractions.length }"
        :style="CSS_VARS"
        :dark="$vuetify.theme.dark"
      >
        <div class="minimized-windows-container">
          <EcsWindow
            v-for="interaction in minimizedWindowInteractions"
            :key="interaction.interactionId"
            :ref="`ecs-window-${interaction.interactionId}`"
            :interaction-id="interaction.interactionId"
            :title="interaction.formattedCustomerFullName"
            :minimized-height="minimizedHeight"
            :minimized-width="minimizedWidth"
            :restored-height="restoredHeight"
            :restored-width="restoredWidth"
            item-class="minimized-item"
            item-content-class="minimized-item-content"
            @window-mounted="handleMinimizedWindowMount"
            @restored-window="handleMinimizedWindowRestore"
            @closed-window="handleMinimizedWindowClosed"
          />
        </div>
        <v-menu
          v-if="hiddenInteractions.length"
          v-model="hiddenWindowsMenuOpened"
          class="more-menu"
          transition="scroll-x-reverse-transition"
          offset-y
          top
          content-class="my-class"
          :z-index="10"
        >
          <template v-slot:activator="{ on }">
            <div class="more-menu-button">
              <v-badge
                :content="totalHiddenInteractionsWithNotifications"
                :value="badgeValue"
                color="primary"
                offset-x="10"
              >
                <v-btn width="28" height="28" class="surface lighten-1" fab :ripple="false" v-on="on">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </v-badge>
            </div>
          </template>
          <v-list dense class="hidden-windows">
            <v-list-item
              v-for="(item, i) in hiddenInteractions"
              :key="i"
              class="hidden-window elevation-3 surface lighten-1"
              @click="handleHiddenItemClick(item)"
            >
              <v-list-item-avatar
                v-show="getNumberOfNotificationsForInteraction(item.interactionId)"
                class="hidden-interaction-notification-badge"
                :size="16"
                color="primary"
              >
                <span class="white--text">{{ getNumberOfNotificationsForInteraction(item.interactionId) }}</span>
              </v-list-item-avatar>
              <v-list-item-title class="hidden-window-title">
                {{ item | title }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Muuri from 'muuri';

import EcsWindow from '@/components/ecs-window/EcsWindow';
import InteractionHeader from '@/components/ecs-interaction/InteractionHeader';
import InteractionToolbar from '@/components/ecs-interaction/InteractionToolbar';
import InteractionFooter from '@/components/ecs-interaction/InteractionFooter';
import InteractionContent from '@/components/ecs-interaction/InteractionContent';

export default {
  name: 'EcsWindowManager',

  components: { InteractionToolbar, InteractionHeader, InteractionFooter, InteractionContent, EcsWindow },

  filters: {
    title(item) {
      return item.formattedCustomerFullName || item.receptor.name;
    }
  },

  props: {
    interactions: {
      type: Array,
      default() {
        return [];
      }
    },
    minimizedWindowsRightOffset: {
      type: Number,
      default: 64
    },
    minimizedHeight: {
      type: Number,
      default: 24
    },
    minimizedWidth: {
      type: Number,
      default: 240
    },
    restoredHeight: {
      type: Number,
      default: 500
    },
    restoredWidth: {
      type: Number,
      default: 480
    }
  },

  data() {
    return {
      hiddenWindowsMenuOpened: false,
      windowsGrid: null,
      minimizedWindowsGrid: null,
      windowManagerWidth: null
    };
  },

  computed: {
    ...mapGetters('NOTIFICATIONS', ['getNumberOfNotificationsForInteraction']),

    ...mapGetters('ELEMENTS', ['getElementsForInteraction']),

    ...mapGetters('WINDOWS', [
      'getOpenWindowsInteractionIds',
      'getInteractionWindow',
      'getMinimizedWindowsInteractionIds'
    ]),

    openWindowInteractions() {
      return this.interactions
        .filter(interaction => (interaction.isTelephony ? !interaction.isHistoric : true))
        .filter(interaction => this.getOpenWindowsInteractionIds.includes(interaction.interactionId));
    },

    minimizedWindowInteractions() {
      return this.interactions.filter(interaction =>
        this.getMinimizedWindowsInteractionIds.includes(interaction.interactionId)
      );
    },

    hiddenInteractions() {
      let hiddenWindows = [];
      let hiddenInteractions = [];
      if (this.minimizedWindowsGrid) {
        hiddenWindows = this.minimizedWindowsGrid
          .getItems()
          .filter(i => !i.isVisible())
          .map(i => i.getElement().getAttribute('id'));
        hiddenInteractions = this.minimizedWindowInteractions.filter(i => hiddenWindows.includes(i.interactionId));
      }
      return hiddenInteractions;
    },

    CSS_VARS() {
      return {
        '--windows-manager-width': this.windowManagerWidth ? `${this.windowManagerWidth}px` : `0px`,
        '--minimized-container-offset': `${this.minimizedWindowsRightOffset}px`
      };
    },

    totalHiddenInteractionsWithNotifications() {
      return this.hiddenInteractions
        .map(i => this.getNumberOfNotificationsForInteraction(i.interactionId))
        .reduce((accum, current) => {
          accum += current;
          return accum;
        }, 0);
    },

    badgeValue() {
      return !this.hiddenWindowsMenuOpened && this.totalHiddenInteractionsWithNotifications;
    }
  },

  watch: {
    'openWindowInteractions.length'() {
      this.setFirstOpenedInteractionAsActive();
    },
    async windowManagerWidth(newValue) {
      if (newValue > 0) {
        await this.$nextTick();
        const items = this.minimizedWindowsGrid.getItems();
        this.minimizedWindowsGrid = this.minimizedWindowsGrid.show(items, { layout: 'instant' });
        this.executeGridLayout(this.minimizedWindowsGrid);
      }
    }
  },

  mounted() {
    this.setFirstOpenedInteractionAsActive();
    this.initializeWindowsContainer();
    this.initializeMinimizedWindowsContainer();
    this.setWindowManagerWidth();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    try {
      window.removeEventListener('resize', this.handleResize);
      this.windowsGrid && this.windowsGrid.destroy(true);
      if (this.minimizedWindowsGrid) {
        this.minimizedWindowsGrid.destroy(true);
      }
    } catch (error) {}
  },

  sockets: {
    INTERACTION_REMOVED_FROM_AGENT_EVENT(event) {
      this.removeWindowFromMuuriGrid(event.interactionId, this.windowsGrid);
      this.removeWindowFromMuuriGrid(event.interactionId, this.minimizedWindowsGrid);
    }
  },

  methods: {
    ...mapActions('WINDOWS', ['restoreInteractionWindow', 'minimizeInteractionWindow', 'setFocusedInteractionId']),
    ...mapActions('INTERACTIONS', ['removeActiveInteraction']),

    handleHiddenItemClick({ interactionId }) {
      this.$refs[`ecs-window-${interactionId}`][0].handleRestoreWindow();
    },

    handleWindowMaximized(interactionId) {
      const windowsToMinimize = this.openWindowInteractions.filter(i => i.interactionId !== interactionId);

      windowsToMinimize.forEach(window => {
        this.minimizeInteractionWindow(window.interactionId);
        this.handleWindowMinimized(window.interactionId);
      });
    },

    async restoreMaximizeWindows(interactionId) {
      const maximizedWindows = this.openWindowInteractions.filter(i => i.interactionId !== interactionId);

      maximizedWindows.forEach(window => {
        this.restoreInteractionWindow(window.interactionId);
      });
      await this.$nextTick();
      if (maximizedWindows) {
        await this.executeGridLayout(this.windowsGrid);
      }
    },

    handleWindowMinimized(interactionId) {
      this.removeWindowFromMuuriGrid(interactionId, this.windowsGrid);
    },
    async handleMinimizedWindowRestore(interactionId) {
      this.removeWindowFromMuuriGrid(interactionId, this.minimizedWindowsGrid);
      await this.restoreMaximizeWindows(interactionId);
      this.checkIfWindowWasHidden(interactionId);
    },

    initializeWindowsContainer() {
      if (this.windowsGrid) {
        return;
      }
      this.windowsGrid = new Muuri('.windows-container', {
        dragEnabled: true,
        dragHandle: '.ecs-window-header',
        layout: this.createWindowsCustomLayout
      });
    },

    initializeMinimizedWindowsContainer() {
      if (this.minimizedWindowsGrid) {
        return;
      }
      this.minimizedWindowsGrid = new Muuri('.minimized-windows-container', {
        dragEnabled: true,
        layout: this.createMinimizedWindowsCustomLayout
      });
    },

    addWindowToMuuriGrid(interactionId, grid) {
      const DOMElement = document.getElementById(interactionId);
      if (!this.getGridElementById(interactionId, grid)) {
        grid.add([DOMElement]);
      }
    },

    removeWindowFromMuuriGrid(interactionId, grid, removeElements = true) {
      const gridElement = this.getGridElementById(interactionId, grid);

      if (gridElement && gridElement._element && gridElement._element.parentNode) {
        this.removeActiveInteraction(interactionId);
        grid.remove([gridElement], { removeElements });
      }
    },

    createWindowsCustomLayout(grid, layoutId, items, width, height, callback) {
      const layout = {
        id: layoutId,
        items,
        slots: [],
        styles: {}
      };
      grid.refreshItems();
      let item;
      let m;
      let x = 0;
      let y = 0;
      let w = 0;
      let h = 0;

      for (let i = 0; i < items.length; i++) {
        item = items[i];
        x += w;
        m = item.getMargin();
        w = item.getWidth() + m.left + m.right;
        h = item.getHeight() + m.top + m.bottom;
        const itemsOverflowsContainerWidth = width < x + w;
        if (itemsOverflowsContainerWidth) {
          x = 0;
          y += h;
        }
        layout.slots.push(x, y);
      }
      // Set the CSS styles that should be applied
      // to the grid element.
      layout.styles.width = `${w}px`;
      layout.styles.height = `${h}px`;

      // When the layout is fully computed
      // let's call the callback function and
      // provide the layout object as it's argument.
      callback(layout);
    },

    createMinimizedWindowsCustomLayout(grid, layoutId, items, width, height, callback) {
      const layout = {
        id: layoutId,
        items,
        slots: [],
        styles: {}
      };

      let item;
      let m;
      let x = 0;
      let w = 0;
      let h = 0;
      const hiddenItems = [];
      const shownItems = [];

      for (let i = 0; i < items.length; i++) {
        item = items[i];
        x += w;
        m = item.getMargin();
        w = item.getWidth() + m.left + m.right;
        h = item.getHeight() + m.top + m.bottom;
        const itemsOverflowsContainerWidth = width < x + w;
        if (itemsOverflowsContainerWidth) {
          hiddenItems.push(item);
        } else {
          layout.slots.push(x, 0);
          shownItems.push(item);
        }
      }
      grid.hide(hiddenItems);
      grid.show(shownItems);
      // Set the CSS styles that should be applied
      // to the grid element.
      layout.styles.width = `${w}px`;
      layout.styles.height = `${h}px`;

      // When the layout is fully computed
      // let's call the callback function and
      // provide the layout object as it's argument.
      callback(layout);
    },

    handleOpenWindowMount(interactionId) {
      if (this.windowsGrid) {
        this.addWindowToMuuriGrid(interactionId, this.windowsGrid);
        this.checkIfWindowWasRestoredFromSidebar(interactionId);
        this.restoreMaximizeWindows(interactionId);
      }
    },

    handleMinimizedWindowMount(interactionId) {
      if (this.minimizedWindowsGrid) {
        this.addWindowToMuuriGrid(interactionId, this.minimizedWindowsGrid);
      }
    },

    handleOpenWindowClosed(interactionId) {
      this.removeWindowFromMuuriGrid(interactionId, this.windowsGrid);
    },

    handleMinimizedWindowClosed(interactionId) {
      this.removeWindowFromMuuriGrid(interactionId, this.minimizedWindowsGrid);
      this.checkIfWindowWasHidden(interactionId);
    },

    checkIfWindowWasRestoredFromSidebar(interactionId) {
      this.removeWindowFromMuuriGrid(interactionId, this.minimizedWindowsGrid, false);
    },

    checkIfWindowWasHidden(interactionId) {
      const element = this.getGridElementById(interactionId, this.minimizedWindowsGrid);
      const windowWasHidden = element ? !element.isVisible() : false;
      if (!windowWasHidden) {
        this.displayHiddenMinimizedWindow();
      }
    },

    displayHiddenMinimizedWindow() {
      const gridElement = this.minimizedWindowsGrid.getItems().find(i => !i.isVisible());
      if (!gridElement) {
        return;
      }
      this.minimizedWindowsGrid.show([gridElement]);
    },

    getGridElementById(interactionId, grid) {
      return grid.getItems().find(i => i.getElement().getAttribute('id') === interactionId);
    },

    handleResize() {
      this.setWindowManagerWidth();
    },

    setWindowManagerWidth() {
      this.windowManagerWidth = this.$refs.windowManager.clientWidth;
    },
    // Added timeout to wait for window to finish resizing before performing the grid layout
    async executeGridLayout(grid) {
      grid.refreshItems();
      await this.$nextTick();
      await this.sleep(400);
      grid.layout();
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    setFirstOpenedInteractionAsActive() {
      if (this.openWindowInteractions.length) {
        const firstInteraction = this.openWindowInteractions[0];
        this.setFocusedInteractionId(firstInteraction.interactionId);
      } else {
        this.setFocusedInteractionId(null);
      }
    }
  }
};
</script>

<style src="./scoped.css" scoped />
<style>
.include-minimized-windows .maximized {
  height: calc(100% - 60px);
}
</style>
